import { useCallback, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { notify } from "../../helper/notify";
import { useTranslation } from "react-i18next";
import { clearInviteState, submitInvite } from "../../features/invite/invite.slice";
import { useAppDispatch, useAppSelector } from "../../hooks/useRedux";

interface AddFreelancerModalProps {
    show: boolean;
    onClose: () => void;
}

const AddFreelancerModal = ({ show, onClose }: AddFreelancerModalProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [inviteEmail, setInviteEmail] = useState("");
    const [invitedFriends, setInvitedFriends] = useState<string[]>([]);

    const { loading: inviteLoading } = useAppSelector(
        (state) => state.invite || { loading: false }
    );

    const handleClose = () => {
        setInviteEmail("");
        setInvitedFriends([]);
        dispatch(clearInviteState());
        onClose();
    };

    const handleAddInvite = () => {
        const email = inviteEmail.trim();

        if (!email) return;

        if (invitedFriends.includes(email)) {
            toast.warning(t("EMAIL_ALREADY_ADDED") as String);
            return;
        }

        setInvitedFriends((prev) => [...prev, email]);
        setInviteEmail("");
    };

    const handleRemoveInvite = (email: string) => {
        setInvitedFriends((prev) => prev.filter((e) => e !== email));
        dispatch(clearInviteState());
    };

    const handleSubmitInvites = useCallback(async () => {
        if (invitedFriends.length === 0) {
            notify.error(t("ADD_AT_LEAST_ONE_EMAIL") as String);
            return;
        }

        try {
            const result = await dispatch(submitInvite(invitedFriends));

            if (submitInvite.fulfilled.match(result)) {
                notify.success(t("INVITATIONS_SENT_SUCCESS") as String);
                handleClose();
            } else {
                notify.error(
                    result.error?.message || t("INVITATION_FAILED") as String
                );
            }
        } catch {
            notify.error(t("INVITATION_FAILED") as String);
        }
    }, [dispatch, invitedFriends]);

    return (
        <Modal
            show={show}
            onHide={handleClose}
            centered
            className="invite-friends-modal"
        >
            <Modal.Header className="invite-modal-header">
                <div className="invite-modal-header__lead">
                    {/* Decorative violet paper-plane icon. Sits inside a
                        soft halo that pulses subtly so the modal feels
                        alive on entry without adding motion noise. */}
                    <span className="invite-modal-header__icon" aria-hidden="true">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </span>
                    <Modal.Title className="invite-modal-title">
                        {t("INVITE_FREELANCERS")}
                    </Modal.Title>
                </div>

                <button className="invite-modal-close" onClick={handleClose} aria-label={t("CANCEL") as string}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </Modal.Header>

            <Modal.Body className="invite-modal-body">
                <p className="invite-modal-description">
                    {t("INVITE_FREELANCERS_WORKING_WITH_YOU")}
                </p>

                {/* Invite chips list — each email gets its own pill.
                    Renders only when there's at least one invite so the
                    empty-state helper below can take over otherwise. */}
                {invitedFriends.length > 0 && (
                    <div className="invited-friends-list" role="list">
                        {invitedFriends.map((email, i) => (
                            <div
                                key={email}
                                className="invited-friend-item"
                                role="listitem"
                                /* Stagger each chip's entrance so multi-invite
                                   sessions read as a small, satisfying cascade
                                   instead of everything popping in together. */
                                style={{ animationDelay: `${i * 60}ms` }}
                            >
                                <span className="invited-friend-avatar" aria-hidden="true">
                                    {email.charAt(0).toUpperCase()}
                                </span>
                                <span className="invited-friend-email">{email}</span>
                                <button
                                    className="remove-invite-btn"
                                    onClick={() => handleRemoveInvite(email)}
                                    aria-label={`Remove ${email}`}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <div className="invite-input-section">
                    <label className="invite-input-label">
                        {t("INVITE_FREELANCER")}
                    </label>

                    <div className="invite-input-wrapper">
                        <Form.Control
                            type="email"
                            placeholder={t("ENTER_FREELANCER_EMAIL")}
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                        />
                        {/* <svg className="email-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" />
                            <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" />
                        </svg> */}
                        <Button
                            className="invite-add-btn "
                            onClick={handleAddInvite}
                        >
                            {t("ADD")}
                        </Button>
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer className="invite-modal-footer">
                <Button
                    className="invite-cancel-btn"
                    variant="outline-primary"
                    onClick={handleClose}
                    disabled={inviteLoading}
                >
                    {t("CANCEL")}
                </Button>

                <Button
                    className="invite-submit-btn"
                    variant="primary"
                    onClick={handleSubmitInvites}
                    disabled={inviteLoading}
                >
                    {inviteLoading ? t("SENDING") : t("SUBMIT")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddFreelancerModal;
